import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Crown, Sparkles } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PeepingLogo } from "@/components/PeepingLogo";
import { SandboxNotice, StatusPill, fmtMoney, fmtDate } from "@/components/Peep";

function PlanCard({ p, onBuy }) {
  const aa = p.scope === "all_access";
  const q = p.quote;
  return (
    <div data-testid={`plan-${aa ? "all-access" : p.creator?.handle}`} className={`rounded-2xl border p-4 ${aa ? "border-[#d42be8]/60 bg-gradient-to-br from-[#6a00ff]/25 to-[#d42be8]/10" : "border-white/10 bg-white/[0.03]"}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="flex items-center gap-1.5 font-bold">{aa ? <Sparkles size={15} className="text-[#d42be8]" /> : <Crown size={15} className="text-[#c9a6ff]" />} {p.name}</p>
          <p className="mt-0.5 text-xs text-[#a7a0af]">{p.description}</p>
        </div>
        <p className="shrink-0 text-lg font-extrabold text-brand-gradient">{p.price === null ? "—" : fmtMoney(p.price)}<span className="text-xs text-[#a7a0af]">{p.price === null ? "" : "/mo"}</span></p>
      </div>
      {q.can_purchase && q.proration_credit > 0 && (
        <p data-testid="plan-upgrade-credit" className="mt-2 text-xs text-[#f0a8fa]">Upgrade credit {fmtMoney(q.proration_credit)} from your current memberships — due today {fmtMoney(q.amount_due)}. Separate memberships end automatically, so you're never charged twice.</p>
      )}
      {q.can_purchase ? (onBuy ? (
        <button onClick={() => onBuy(p)} data-testid={`plan-buy-${aa ? "all-access" : p.creator?.handle}`} className="btn-brand mt-3 w-full rounded-xl py-3 text-sm font-semibold">{q.proration_credit > 0 ? "Upgrade to All Access" : "Subscribe"}</button>
      ) : null) : (
        <p data-testid={`plan-reason-${aa ? "all-access" : p.creator?.handle}`} className="mt-3 rounded-xl border border-white/10 px-3 py-2 text-center text-xs text-[#a7a0af]">{q.reason}</p>
      )}
    </div>
  );
}

function SubRow({ s, creators, reload }) {
  const [pick, setPick] = useState("");
  const act = async (path, body, msg) => {
    try {
      await api.post(`/memberships/${s.id}/${path}`, body);
      toast.success(msg);
      reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };
  const live = ["active", "past_due"].includes(s.status);
  return (
    <div data-testid={`my-sub-${s.id}`} className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
      <div className="flex items-center justify-between gap-2">
        <p className="font-semibold">{s.plan_name}</p>
        <StatusPill status={s.status === "active" && s.cancel_at_period_end ? "canceling" : s.status} testid={`my-sub-status-${s.id}`} />
      </div>
      <p className="mt-1 text-xs text-[#a7a0af]">
        {live ? (s.cancel_at_period_end ? `Access until ${fmtDate(s.current_period_end)}` : s.status === "past_due" ? `Payment failed — access until ${fmtDate(s.grace_until)}` : `Renews ${fmtDate(s.current_period_end)}`) : `Ended ${fmtDate(s.ended_at)}${s.end_reason ? ` · ${s.end_reason.replace(/_/g, " ")}` : ""}`}
      </p>
      {live && (
        <div className="mt-3 flex flex-wrap gap-2">
          {!s.cancel_at_period_end && <button onClick={() => act("cancel", {}, "Cancellation confirmed")} data-testid={`cancel-sub-${s.id}`} className="rounded-full border border-white/15 px-3 py-1.5 text-xs">Cancel membership</button>}
          {s.cancel_at_period_end && s.status === "active" && <button onClick={() => act("resume", {}, "Membership resumed")} data-testid={`resume-sub-${s.id}`} className="btn-brand rounded-full px-3 py-1.5 text-xs font-semibold">Resume</button>}
          {s.scope === "all_access" && !s.cancel_at_period_end && s.status === "active" && (
            <div className="flex items-center gap-2">
              <select value={pick} onChange={(e) => setPick(e.target.value)} data-testid={`downgrade-pick-${s.id}`} className="field rounded-full px-3 py-1.5 text-xs">
                <option value="">Downgrade to…</option>
                {creators.map((c) => <option key={c.id} value={c.id}>{c.display_name}</option>)}
              </select>
              {pick && <button onClick={() => act("downgrade", { creator_id: pick }, "Downgrade scheduled")} data-testid={`downgrade-sub-${s.id}`} className="rounded-full border border-[#d42be8]/50 px-3 py-1.5 text-xs">Confirm</button>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function Membership() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isReview = user?.role === "platform_owner_review";
  const isGuestReviewer = user?.role === "external_premium_reviewer";
  const isStaff = user?.role === "creator" || user?.role === "super_admin";
  const [plans, setPlans] = useState([]);
  const [subs, setSubs] = useState([]);
  const [history, setHistory] = useState([]);
  const [loadError, setLoadError] = useState("");
  const load = useCallback(() => {
    setLoadError("");
    api.get("/memberships/plans").then((r) => setPlans(r.data.plans)).catch(() => setLoadError("We couldn't load memberships. Please try again."));
    api.get("/memberships/me").then((r) => setSubs(r.data.subscriptions)).catch(() => {});
    api.get("/pay/history").then((r) => setHistory(r.data.history)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);
  const creators = plans.filter((p) => p.scope === "creator").map((p) => ({ id: p.creator_id, display_name: p.creator?.display_name }));

  return (
    <div data-testid="membership-screen" className="px-5 pt-6 pb-6">
      <div className="flex flex-col items-center"><PeepingLogo className="w-40" /></div>
      <h1 className="mt-6 text-2xl font-extrabold">Choose Your Access</h1>
      <p className="text-sm text-[#a7a0af]">One creator, or every creator with PEEPING ALL ACCESS.</p>
      <SandboxNotice className="mt-4" />
      <div className="mt-4 flex flex-col gap-3">
        {loadError && <p data-testid="membership-load-error" className="rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">{loadError} <button onClick={load} className="underline">Retry</button></p>}
        {!loadError && plans.length === 0 && <p className="text-sm text-[#a7a0af]">Memberships are coming soon.</p>}
        {isGuestReviewer ? (
          <p data-testid="reviewer-no-purchase" className="rounded-xl border border-[#d42be8]/40 bg-[#d42be8]/10 px-4 py-3 text-sm text-[#f0a8fa]">Reviewer access — complimentary, no purchase or payment details needed. Plans are shown below for inspection only.</p>
        ) : null}
        {isStaff && <p data-testid="creator-no-purchase" className="rounded-xl border border-[#d42be8]/40 bg-[#d42be8]/10 px-4 py-3 text-sm text-[#f0a8fa]">You're browsing as a {user.role === "creator" ? "creator" : "Super Admin"}. Memberships are bought with subscriber accounts, so plans are shown for viewing only.</p>}
        {isGuestReviewer || isStaff ? plans.map((p) => <PlanCard key={p.id} p={p} onBuy={null} />) : isReview ? (
          <p data-testid="review-no-purchase" className="rounded-xl border border-[#d42be8]/40 bg-[#d42be8]/10 px-4 py-3 text-sm text-[#f0a8fa]">Owner Review access — every creator and All Access is already unlocked. No purchase needed.</p>
        ) : plans.map((p) => <PlanCard key={p.id} p={p} onBuy={() => navigate(`/app/checkout?plan=${p.id}`)} />)}
      </div>

      <h2 className="mt-8 text-lg font-bold">My memberships</h2>
      <div className="mt-3 flex flex-col gap-2">
        {subs.length === 0 && <p data-testid="my-subs-empty" className="text-sm text-[#a7a0af]">No memberships yet.</p>}
        {subs.map((s) => <SubRow key={`${s.id}-${s.status}-${s.cancel_at_period_end}`} s={s} creators={creators} reload={load} />)}
      </div>

      <h2 className="mt-8 text-lg font-bold">Purchase history</h2>
      <ul data-testid="purchase-history" className="mt-3 flex flex-col gap-1">
        {history.length === 0 && <li className="text-sm text-[#a7a0af]">No payments yet.</li>}
        {history.map((h) => (
          <li key={h.id} data-testid="history-row" className="flex items-center justify-between border-t border-white/5 py-2 text-sm">
            <span><span className="capitalize">{h.type}</span> · {h.plan_name}<span className="block text-[0.65rem] text-[#a7a0af]">{fmtDate(h.created_at)}</span></span>
            <span className="flex items-center gap-2">{fmtMoney(h.amount)} {h.provider === "sandbox" && <span data-testid="history-sandbox-pill" className="rounded-full border border-amber-300/40 px-2 py-0.5 text-[0.6rem] uppercase text-amber-200">Sandbox</span>} <StatusPill status={h.status} /></span>
          </li>
        ))}
      </ul>
    </div>
  );
}
